import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tags, Plus, Search, Edit, Trash2, Loader2, X, Package, Weight } from 'lucide-react';

interface PriceCategory {
  id: number;
  name: string;
  description?: string;
  type?: string;
  basePrice?: number;
  pricePerKg?: number;
  pricePerKm?: number;
  minPrice?: number;
  maxPrice?: number;
  minWeight?: number;
  maxWeight?: number;
  applicableZones?: string;
  applicableServices?: string;
  isActive: boolean;
  branchId?: number;
  priority?: number;
  createdAt?: string;
}

export default function PriceCategoriesPage() {
  const [categories, setCategories] = useState<PriceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<PriceCategory | null>(null);
  const [form, setForm] = useState<any>({
    name: '', description: '', type: 'basic', basePrice: 0, pricePerKg: 0,
    pricePerKm: 0, minPrice: 0, maxPrice: 0, minWeight: 0, maxWeight: 100,
    applicableZones: '', applicableServices: '', isActive: true, priority: 0
  });

  useEffect(() => { loadCategories(); }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/pricecategories');
      if (res.data?.success) {
        setCategories(Array.isArray(res.data.data) ? res.data.data : []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingCategory(null);
    setForm({
      name: '', description: '', type: 'basic', basePrice: 0, pricePerKg: 0,
      pricePerKm: 0, minPrice: 0, maxPrice: 0, minWeight: 0, maxWeight: 100,
      applicableZones: '', applicableServices: '', isActive: true, priority: 0
    });
    setShowModal(true);
  };

  const openEdit = (c: PriceCategory) => {
    setEditingCategory(c);
    setForm({
      name: c.name || '', description: c.description || '', type: c.type || 'basic',
      basePrice: c.basePrice || 0, pricePerKg: c.pricePerKg || 0,
      pricePerKm: c.pricePerKm || 0, minPrice: c.minPrice || 0, maxPrice: c.maxPrice || 0,
      minWeight: c.minWeight || 0, maxWeight: c.maxWeight || 100,
      applicableZones: c.applicableZones || '', applicableServices: c.applicableServices || '',
      isActive: c.isActive, priority: c.priority || 0
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await api.put(`/api/pricecategories/${editingCategory.id}`, form);
      } else {
        await api.post('/api/pricecategories', form);
      }
      setShowModal(false);
      loadCategories();
    } catch (err: any) {
      alert(err.response?.data?.message || 'حدث خطأ');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الفئة؟')) return;
    try {
      await api.delete(`/api/pricecategories/${id}`);
      loadCategories();
    } catch (err: any) {
      alert(err.response?.data?.message || 'حدث خطأ');
    }
  };

  const filtered = categories.filter(c =>
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.type || '').toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = categories.filter(c => c.isActive).length;
  const avgPrice = categories.length > 0
    ? (categories.reduce((sum, c) => sum + (c.basePrice || 0), 0) / categories.length).toFixed(2)
    : '0.00';

  const getTypeLabel = (type: string | undefined) => {
    switch (type?.toLowerCase()) {
      case 'basic': return 'أساسي';
      case 'weight': return 'وزن';
      case 'distance': return 'مسافة';
      case 'express': return 'سريع';
      case 'premium': return 'مميز';
      default: return 'مخصوص';
    }
  };

  const getTypeColor = (type: string | undefined) => {
    switch (type?.toLowerCase()) {
      case 'basic': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'weight': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'distance': return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'express': return 'bg-red-50 text-red-700 border-red-200';
      case 'premium': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 transition';

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
            <Tags className="w-6 h-6 text-white" />
          </div>
          <div>
            <span>{'فئات الأسعار'}</span>
            <p className="text-sm text-gray-500 font-normal mt-1">{'إدارة فئات التسعير والشحن'}</p>
          </div>
        </h1>
        <Button onClick={openCreate} className="bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg">
          <Plus className="w-5 h-5 ml-2" />
          <span>{'إضافة فئة جديدة'}</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center"><Tags className="w-5 h-5 text-orange-600" /></div></div>
          <p className="text-sm text-gray-500">{'إجمالي الفئات'}</p>
          <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center"><Package className="w-5 h-5 text-green-600" /></div></div>
          <p className="text-sm text-gray-500">{'الفئات النشطة'}</p>
          <p className="text-2xl font-bold text-green-700">{activeCount}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Weight className="w-5 h-5 text-blue-600" /></div></div>
          <p className="text-sm text-gray-500">{'متوسط السعر'}</p>
          <p className="text-2xl font-bold text-blue-700">{avgPrice} {'ج.م'}</p>
        </CardContent></Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="relative mb-6">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder={'بحث باسم الفئة...'} value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 transition" />
          </div>
          {loading ? (
            <div className="p-12 text-center"><Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto" /><p className="text-gray-500 mt-4">{'جاري التحميل...'}</p></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center"><Tags className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-semibold text-gray-900 mb-2">{'لا توجد فئات أسعار'}</h3></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{'اسم الفئة'}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{'النوع'}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{'السعر الأساسي'}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{'لكل كجم'}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{'لكل كم'}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{'الحالة'}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{'الإجراءات'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((category) => (
                    <tr key={category.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white"><Tags className="w-5 h-5" /></div>
                          <div>
                            <p className="font-semibold text-gray-900">{category.name}</p>
                            {category.description && <p className="text-xs text-gray-500">{category.description}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getTypeColor(category.type)}`}>
                          {getTypeLabel(category.type)}
                        </span>
                      </td>
                      <td className="py-4 px-5"><span className="font-bold text-orange-600">{(category.basePrice || 0).toFixed(2)} {'ج.م'}</span></td>
                      <td className="py-4 px-5"><span className="text-gray-700">{(category.pricePerKg || 0).toFixed(2)} {'ج.م'}</span></td>
                      <td className="py-4 px-5"><span className="text-gray-700">{(category.pricePerKm || 0).toFixed(2)} {'ج.م'}</span></td>
                      <td className="py-4 px-5">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${category.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {category.isActive ? 'نشط' : 'غير نشط'}
                        </span>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(category)} className="p-2 hover:bg-orange-50 rounded-lg text-gray-500 hover:text-orange-600 transition" title="{'تعديل'}"><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(category.id)} className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition" title="{'حذف'}"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">{editingCategory ? 'تعديل الفئة' : 'إضافة فئة سعرية'}</h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label>{'اسم الفئة *'}</Label><Input value={form.name} onChange={(e: any) => setForm({...form, name: e.target.value})} required className={inputClass} /></div>
                  <div><Label>{'النوع'}</Label>
                    <select value={form.type} onChange={(e: any) => setForm({...form, type: e.target.value})} className={inputClass}>
                      <option value="basic">{'أساسي'}</option>
                      <option value="weight">{'وزن'}</option>
                      <option value="distance">{'مسافة'}</option>
                      <option value="express">{'سريع'}</option>
                      <option value="premium">{'مميز'}</option>
                    </select>
                  </div>
                  <div className="md:col-span-2"><Label>{'الوصف'}</Label><textarea value={form.description} onChange={(e: any) => setForm({...form, description: e.target.value})} className={inputClass} rows={2} /></div>
                  <div><Label>{'السعر الأساسي'}</Label><Input type="number" step="0.01" value={form.basePrice} onChange={(e: any) => setForm({...form, basePrice: parseFloat(e.target.value) || 0})} className={inputClass} /></div>
                  <div><Label>{'السعر لكل كجم'}</Label><Input type="number" step="0.01" value={form.pricePerKg} onChange={(e: any) => setForm({...form, pricePerKg: parseFloat(e.target.value) || 0})} className={inputClass} /></div>
                  <div><Label>{'السعر لكل كم'}</Label><Input type="number" step="0.01" value={form.pricePerKm} onChange={(e: any) => setForm({...form, pricePerKm: parseFloat(e.target.value) || 0})} className={inputClass} /></div>
                  <div><Label>{'الأولوية'}</Label><Input type="number" value={form.priority} onChange={(e: any) => setForm({...form, priority: parseInt(e.target.value) || 0})} className={inputClass} /></div>
                  <div><Label>{'الحد الأدنى للسعر'}</Label><Input type="number" step="0.01" value={form.minPrice} onChange={(e: any) => setForm({...form, minPrice: parseFloat(e.target.value) || 0})} className={inputClass} /></div>
                  <div><Label>{'الحد الأقصى للسعر'}</Label><Input type="number" step="0.01" value={form.maxPrice} onChange={(e: any) => setForm({...form, maxPrice: parseFloat(e.target.value) || 0})} className={inputClass} /></div>
                  <div><Label>{'الحد الأدنى للوزن (كجم)'}</Label><Input type="number" step="0.01" value={form.minWeight} onChange={(e: any) => setForm({...form, minWeight: parseFloat(e.target.value) || 0})} className={inputClass} /></div>
                  <div><Label>{'الحد الأقصى للوزن (كجم)'}</Label><Input type="number" step="0.01" value={form.maxWeight} onChange={(e: any) => setForm({...form, maxWeight: parseFloat(e.target.value) || 0})} className={inputClass} /></div>
                  <div><Label>{'المناطق المطبقة'}</Label><Input value={form.applicableZones} onChange={(e: any) => setForm({...form, applicableZones: e.target.value})} className={inputClass} /></div>
                  <div><Label>{'الخدمات المطبقة'}</Label><Input value={form.applicableServices} onChange={(e: any) => setForm({...form, applicableServices: e.target.value})} className={inputClass} /></div>
                  <div className="md:col-span-2 flex items-center gap-2"><input type="checkbox" id="isActive" checked={form.isActive} onChange={(e) => setForm({...form, isActive: e.target.checked})} className="w-4 h-4" /><label htmlFor="isActive" className="text-sm font-medium">{'نشط'}</label></div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">{'إلغاء'}</Button>
                  <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700">{'حفظ'}</Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
